import { useState } from "react"
import { useNavigate } from "react-router-dom"
import { motion, AnimatePresence } from "framer-motion"
import { LayoutDashboard, Loader, LogOut, LogIn, X, Menu, ChevronRight } from "lucide-react"
import { useAuth } from "../contexts/AuthProvider"
import { useUser } from "../contexts/UserContext"

const FONT = '"Plus Jakarta Sans", sans-serif'

const NAV_LINKS = [
  { label: "Jobs",     path: "/job-listings" },
  { label: "Pricing",  path: "/pricing" },
  { label: "About",    path: "/about-us" },
  { label: "Support",  path: "/support" },
]

const Header = ({ onUserChange, showGotoConsole = true, hideFreeTrial = false }) => {
  const navigate = useNavigate()
  const [mobileOpen, setMobileOpen] = useState(false)
  const { user, logout } = useAuth()
  const { organizationName } = useUser()
  const [isLoggingOut, setIsLoggingOut] = useState(false)

  const go = (path) => { setMobileOpen(false); navigate(path) }

  const displayName = organizationName || user?.name || "there"

  function RightCTA() {
    if (!user) return (
      <div className="flex items-center gap-1.5 shrink-0">
        <button
          onClick={() => go("/login")}
          className="text-[13px] font-medium text-zinc-600 hover:text-zinc-900 transition-colors px-3 py-2 rounded-lg hover:bg-zinc-100"
          style={{ fontFamily: FONT }}
        >
          Log in
        </button>
        {!hideFreeTrial && (
          <button
            onClick={() => go("/register")}
            className="flex items-center gap-1 rounded-lg px-4 py-2 text-[13px] font-semibold bg-zinc-900 hover:bg-zinc-700 text-white transition-colors whitespace-nowrap cursor-pointer"
            style={{ fontFamily: FONT }}
          >
            Get Started <ChevronRight size={13} />
          </button>
        )}
      </div>
    )
    if (user?.authenticated) return (
      <div className="flex items-center gap-2 shrink-0">
        {showGotoConsole && (
          <button
            onClick={() => go("/profile")}
            className="flex items-center gap-2 rounded-lg px-4 py-2 text-[13px] font-semibold bg-zinc-900 hover:bg-zinc-700 text-white transition-colors cursor-pointer"
            style={{ fontFamily: FONT }}
          >
            <div className="w-5 h-5 rounded-full bg-white/20 flex items-center justify-center shrink-0">
              <span className="text-[9px] font-black text-white leading-none">
                {displayName.charAt(0).toUpperCase()}
              </span>
            </div>
            Console
          </button>
        )}
      </div>
    )
    return null
  }

  return (
    <header className="fixed top-0 z-50 flex w-full items-center justify-center px-3 py-3 sm:px-4">

      {/* ── Desktop nav ── */}
      <nav
        className="hidden md:flex relative w-full max-w-5xl items-center justify-between rounded-xl border border-white bg-white/95 backdrop-blur-md py-1.5 pr-1.5 pl-4 shadow-lg"
        style={{ fontFamily: FONT }}
      >
        <button onClick={() => go("/")} className="cursor-pointer shrink-0 flex items-center gap-2">
          <img src="/icon.png" alt="Resolut" className="w-7 h-7 object-contain rounded-lg" />
          <span className="font-black text-[14px] tracking-[.04em] uppercase text-zinc-900 select-none" style={{ fontFamily: FONT }}>Resolut</span>
        </button>

        <ul className="flex items-center gap-0.5 text-[13px] text-zinc-600">
          {NAV_LINKS.map(link => (
            <li key={link.path}>
              <button
                onClick={() => go(link.path)}
                className="flex items-center rounded-lg px-3 py-2 hover:bg-zinc-100 hover:text-zinc-900 transition-colors duration-150 cursor-pointer"
              >
                {link.label}
              </button>
            </li>
          ))}
        </ul>

        <RightCTA />
      </nav>

      {/* ── Mobile nav ── */}
      <div className="md:hidden relative w-full max-w-5xl">
        <nav
          className="flex w-full items-center justify-between gap-2 rounded-xl border border-white bg-white/95 backdrop-blur-md py-2 pr-2 pl-3 shadow-lg"
          style={{ fontFamily: FONT }}
        >
          <button onClick={() => go("/")} className="cursor-pointer min-w-0 flex items-center gap-2">
            <img src="/icon.png" alt="Resolut" className="w-6 h-6 object-contain rounded-md" />
            <span className="font-black text-[13px] tracking-[.04em] uppercase text-zinc-900 select-none" style={{ fontFamily: FONT }}>Resolut</span>
          </button>

          <div className="flex items-center gap-1.5 shrink-0">
            <RightCTA />
            <button
              onClick={() => setMobileOpen(v => !v)}
              className="flex items-center justify-center w-9 h-9 rounded-lg hover:bg-zinc-100 text-zinc-700 transition-colors cursor-pointer"
            >
              {mobileOpen ? <X size={18} /> : <Menu size={18} />}
            </button>
          </div>
        </nav>

        <AnimatePresence>
          {mobileOpen && (
            <motion.div
              initial={{ opacity: 0, y: -8, scale: 0.98 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -8, scale: 0.98 }}
              transition={{ duration: 0.18, ease: [0.16, 1, 0.3, 1] }}
              className="absolute top-full left-0 right-0 mt-2 bg-white/95 backdrop-blur-md border border-white rounded-xl shadow-xl overflow-hidden"
              style={{ fontFamily: FONT }}
            >
              <ul className="flex flex-col p-2 text-[14px] text-zinc-700">
                {NAV_LINKS.map(link => (
                  <li key={link.path}>
                    <button
                      onClick={() => go(link.path)}
                      className="flex w-full items-center rounded-lg px-3 py-2.5 hover:bg-zinc-100 transition-colors cursor-pointer"
                    >
                      {link.label}
                    </button>
                  </li>
                ))}
              </ul>

              <div className="border-t border-zinc-100 px-4 py-4 flex flex-col gap-2">
                {user?.authenticated ? (
                  <>
                    <div className="flex items-center gap-3 px-3 py-3 bg-zinc-50 rounded-xl">
                      <div className="w-7 h-7 rounded-full bg-zinc-900 text-white text-[12px] font-bold flex items-center justify-center shrink-0">
                        {displayName.charAt(0).toUpperCase()}
                      </div>
                      <span className="text-[13px] font-medium text-zinc-900 truncate">{displayName}</span>
                    </div>
                    {showGotoConsole && (
                      <button
                        onClick={() => go("/profile")}
                        className="bg-zinc-900 hover:bg-zinc-700 text-white text-[13px] font-semibold px-4 py-2.5 rounded-lg w-full flex items-center justify-center gap-2 transition-colors cursor-pointer"
                      >
                        <LayoutDashboard className="w-3.5 h-3.5" /> Console
                      </button>
                    )}
                    <button
                      onClick={async () => {
                        try { setIsLoggingOut(true); await logout(); go("/") }
                        catch (e) { console.error(e) }
                        finally { setIsLoggingOut(false) }
                      }}
                      disabled={isLoggingOut}
                      className="text-[13px] font-medium text-red-500 hover:text-red-600 hover:bg-red-50 px-3 py-2 rounded-lg flex items-center gap-2 transition-colors disabled:opacity-40 cursor-pointer"
                    >
                      {isLoggingOut ? <Loader className="w-4 h-4 animate-spin" /> : <LogOut className="w-4 h-4" />}
                      Sign out
                    </button>
                  </>
                ) : (
                  <>
                    <button
                      onClick={() => go("/login")}
                      className="text-[13px] font-medium text-zinc-700 hover:bg-zinc-50 px-3 py-2.5 rounded-lg flex items-center gap-2 transition-colors cursor-pointer"
                    >
                      <LogIn className="w-4 h-4" /> Log in
                    </button>
                    {!hideFreeTrial && (
                      <button
                        onClick={() => go("/register")}
                        className="bg-zinc-900 hover:bg-zinc-700 text-white text-[13px] font-semibold px-4 py-2.5 rounded-lg w-full text-center transition-colors cursor-pointer"
                      >
                        Get Started
                      </button>
                    )}
                  </>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </header>
  )
}

export default Header
